var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BigNumber } from "bignumber.js";
import querystring from "querystring";
import { TypeRegistry } from "@polkadot/types";
import { getSpecTypes } from "@polkadot/types-known";
import { Metadata } from "@polkadot/types/metadata";
import { expandMetadata } from "@polkadot/types/metadata/decorate";
import { getEnv } from "@ledgerhq/live-env";
/**
 * Get indexer base url.
 *
 * @returns {string}
 */
const getBaseSidecarUrl = () => getEnv("API_POLKADOT_SIDECAR");
/**
 * Returns the full indexer url for en route endpoint.
 *
 * @param {*} route
 *
 * @returns {string}
 */
const getSidecarUrl = (route) => `${getBaseSidecarUrl()}${route || ""}`;
const VALIDATOR_COMISSION_RATIO = 1000000000;
const ELECTION_STATUS_OPTIMISTIC_THRESHOLD = getEnv("POLKADOT_ELECTION_STATUS_THRESHOLD") || 25;
// blocks = 2 minutes 30
/**
 * Fetch Balance from the api.
 *
 * @async
 * @param {string} addr
 *
 * @returns {SidecarAccountBalanceInfo}
 */
const fetchBalanceInfo = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/accounts/${addr}/balance-info`),
    });
    return data;
});
/**
 * Fetch the stash address associated to an account.
 *
 * @async
 * @param {string} addr
 *
 * @returns {string}
 */
const fetchStashAddr = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/pallets/staking/storage/ledger?keys[]=${addr}&key1=${addr}`),
    });
    return (_b = (_a = data.value) === null || _a === void 0 ? void 0 : _a.stash) !== null && _b !== void 0 ? _b : null;
});
/**
 * Fetch the controller address associated to an account.
 *
 * @async
 * @param {string} addr
 *
 * @returns {string}
 */
const fetchControllerAddr = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/pallets/staking/storage/bonded?keys[]=${addr}&key1=${addr}`),
    });
    return (_c = data.value) !== null && _c !== void 0 ? _c : null;
});
/**
 * Fetch the staking info for an account.
 *
 * @async
 * @param {string} addr
 *
 * @returns {SidecarStakingInfo}
 */
const fetchStakingInfo = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/accounts/${addr}/staking-info`),
    });
    return data;
});
/**
 * Returns the list of nominations for an account, with status and associated stake if relevant.
 *
 * @async
 * @param {string} addr
 *
 * @returns {SidecarNominations}
 */
const fetchNominations = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/accounts/${addr}/nominations`),
    });
    return data;
});
/**
 * Returns the blockchain's runtime constants.
 *
 * @async
 * @param {string} addr
 *
 * @returns {Object}
 */
const fetchConstants = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/runtime/constants`),
    });
    return data.consts;
});
/**
 * Returns the activeEra info
 *
 * @async
 *
 * @returns {SidecarPalletStorageItem}
 */
const fetchActiveEra = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl("/pallets/staking/storage/activeEra"),
    });
    return data;
});
/**
 * Fetch the minimum value allowed for a bond
 *
 * @async
 * @param {string} addr
 *
 * @returns {string}
 */
export const getMinimumBondBalance = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield network({
        method: "GET",
        url: getSidecarUrl(`/pallets/staking/storage/minNominatorBond`),
    });
    return (data.value && new BigNumber(data.value)) || new BigNumber(0);
});
/**
 * Fetch a list of validators with some info and indentity.
 * It fetches the list providing a status (all, elected, waiting) and/or a list of
 * addresses (comma-separated or as multiple query params).
 *
 * @async
 * @param {string} status
 * @param {string[]} addresses
 *
 * @returns {SidecarValidators}
 */
const fetchValidators = (network) => (status = "all", addresses) => __awaiter(void 0, void 0, void 0, function* () {
    let params = {};
    if (status) {
        params = Object.assign(Object.assign({}, params), { status });
    }
    if (addresses && addresses.length) {
        params = Object.assign(Object.assign({}, params), { addresses: addresses.join(",") });
    }
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/validators?${querystring.stringify(params)}`),
    });
    return data;
});
/**
 * Fetch the progress info concerning staking.
 *
 * @async
 *
 * @returns {SidecarPalletStakingProgress}
 */
const fetchStakingProgress = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl("/pallets/staking/progress"),
    });
    return data;
});
/**
 * Fetch the transaction params needed to sign a transaction
 *
 * @async
 *
 * @returns {SidecarTransactionMaterial}
 */
const fetchTransactionMaterial = (network) => (
// By default we don't want any metadata.
withMetadata = false) => __awaiter(void 0, void 0, void 0, function* () {
    const params = withMetadata ? "?metadata=scale" : "?noMeta=true";
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl(`/transaction/material${params}`),
    });
    return data;
});
/**
 * Fetch the blockchain specs
 *
 * @async
 *
 * @returns {SidecarRuntimeSpec}
 */
const fetchChainSpec = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "GET",
        url: getSidecarUrl("/runtime/spec"),
    });
    return data;
});
/**
 * Returns true if ElectionStatus is Close.
 * If ElectionStatus is Open, some features must be disabled.
 *
 * @async
 *
 * @returns {boolean}
 */
export const isElectionClosed = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    var _d, _e;
    const progress = yield fetchStakingProgress(network)();
    return !((_e = (_d = progress.electionStatus) === null || _d === void 0 ? void 0 : _d.status) === null || _e === void 0 ? void 0 : _e.Open);
});
/**
 * Returns true if the address is a new account with no balance.
 *
 * @async
 * @param {*} addr
 *
 * @returns {boolean}
 */
export const isNewAccount = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const { nonce, free } = yield fetchBalanceInfo(network)(addr);
    return new BigNumber(0).isEqualTo(nonce) && new BigNumber(0).isEqualTo(free);
});
/**
 * Returns true if the address is a controller, i.e. it is associated to a stash.
 *
 * @async
 * @param {*} addr
 *
 * @returns {boolean}
 */
export const isControllerAddress = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const stash = yield fetchStashAddr(network)(addr);
    return !!stash;
});
/**
 * Returns all addresses that are not validators.
 *
 * @async
 * @param {string[]} - address to verify
 *
 * @returns {string[]} - addresses that are not validators
 */
export const verifyValidatorAddresses = (network) => (validators) => __awaiter(void 0, void 0, void 0, function* () {
    const existingValidators = yield fetchValidators(network)("all", validators);
    const existingIds = existingValidators.map(v => v.accountId);
    return validators.filter(v => !existingIds.includes(v));
});
/**
 * Get all account-related data
 *
 * @async
 * @param {*} addr
 */
export const getAccount = (network, cache) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const balances = yield getBalances(network)(addr);
    const stakingInfo = yield getStakingInfo(network, cache)(addr);
    const nominations = yield getNominations(network)(addr);
    return Object.assign(Object.assign(Object.assign({}, balances), stakingInfo), { nominations });
});
/**
 * Returns all the balances for an account
 *
 * @async
 * @param {*} addr - the account address
 */
const getBalances = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const balanceInfo = yield fetchBalanceInfo(network)(addr);
    // Locked is the highest value among locks
    const totalLocked = balanceInfo.locks.reduce((total, lock) => {
        const amount = new BigNumber(lock.amount);
        if (amount.gt(total)) {
            return amount;
        }
        return total;
    }, new BigNumber(0));
    const balance = new BigNumber(balanceInfo.free);
    const spendableBalance = totalLocked.gt(balance) ? new BigNumber(0) : balance.minus(totalLocked);
    return {
        blockHeight: Number(balanceInfo.at.height),
        balance,
        spendableBalance,
        nonce: Number(balanceInfo.nonce),
        lockedBalance: new BigNumber(totalLocked),
    };
});
/**
 * Returns all staking-related data for an account
 *
 * @async
 * @param {*} addr
 */
export const getStakingInfo = (network, cache) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h, _j, _k, _l;
    const [stash, controller] = yield Promise.all([
        fetchStashAddr(network)(addr),
        fetchControllerAddr(network)(addr),
    ]);
    // If account is not a stash, no need to fetch staking-info (it would return an error)
    if (!controller) {
        return {
            controller: null,
            stash: stash || null,
            unlockedBalance: new BigNumber(0),
            unlockingBalance: new BigNumber(0),
            unlockings: [],
        };
    }
    const [stakingInfo, activeEra, consts] = yield Promise.all([
        fetchStakingInfo(network)(addr),
        fetchActiveEra(network)(),
        getConstants(network, cache)(),
    ]);
    const activeEraIndex = Number(((_f = activeEra.value) === null || _f === void 0 ? void 0 : _f.index) || 0);
    const activeEraStart = Number(((_g = activeEra.value) === null || _g === void 0 ? void 0 : _g.start) || 0);
    const blockTime = new BigNumber(((_h = consts === null || consts === void 0 ? void 0 : consts.babe) === null || _h === void 0 ? void 0 : _h.expectedBlockTime) || 6000); // 6000 ms
    const epochDuration = new BigNumber(((_j = consts === null || consts === void 0 ? void 0 : consts.babe) === null || _j === void 0 ? void 0 : _j.epochDuration) || 2400); // 2400 blocks
    const sessionsPerEra = new BigNumber(((_k = consts === null || consts === void 0 ? void 0 : consts.staking) === null || _k === void 0 ? void 0 : _k.sessionsPerEra) || 6); // 6 sessions
    const eraLength = sessionsPerEra.multipliedBy(epochDuration).multipliedBy(blockTime).toNumber();
    const unlockings = (stakingInfo === null || stakingInfo === void 0 ? void 0 : stakingInfo.staking.unlocking)
        ? (_l = stakingInfo === null || stakingInfo === void 0 ? void 0 : stakingInfo.staking) === null || _l === void 0 ? void 0 : _l.unlocking.map(lock => ({
            amount: new BigNumber(lock.value),
            completionDate: new Date(activeEraStart + (Number(lock.era) - activeEraIndex) * eraLength), // This is an estimation of the date of completion, since it depends on block validation speed
        }))
        : [];
    const now = new Date();
    const unlocked = unlockings.filter(lock => lock.completionDate <= now);
    const unlockingBalance = unlockings.reduce((sum, lock) => sum.plus(lock.amount), new BigNumber(0));
    const unlockedBalance = unlocked.reduce((sum, lock) => sum.plus(lock.amount), new BigNumber(0));
    const numSlashingSpans = Number((stakingInfo === null || stakingInfo === void 0 ? void 0 : stakingInfo.numSlashingSpans) || 0);
    return {
        controller: controller || null,
        stash: stash || null,
        unlockedBalance,
        unlockingBalance,
        unlockings,
        numSlashingSpans,
    };
});
/**
 * Returns nominations for an account including validator address, status and associated stake.
 *
 * @async
 * @param {*} addr
 *
 * @returns {PolkadotNomination[}
 */
export const getNominations = (network) => (addr) => __awaiter(void 0, void 0, void 0, function* () {
    const nominations = yield fetchNominations(network)(addr);
    if (!nominations)
        return [];
    return nominations.targets.map(nomination => ({
        address: nomination.address,
        value: new BigNumber(nomination.value || 0),
        status: nomination.status,
    }));
});
/**
 * Returns all the params from the chain to build an extrinsic (a transaction on Substrate)
 *
 * @async
 */
export const getTransactionParams = (network) => () => __awaiter(void 0, void 0, void 0, function* () {
    const material = yield fetchTransactionMaterial(network)();
    return {
        blockHash: material.at.hash,
        blockNumber: material.at.height,
        chainName: material.chainName,
        genesisHash: material.genesisHash,
        specName: material.specName,
        transactionVersion: material.txVersion,
        specVersion: material.specVersion,
    };
});
/**
 * Broadcast the transaction to the substrate node
 *
 * @async
 * @param {string} extrinsic - the encoded extrinsic to send
 *
 * @returns {string>} - the broadcasted transaction's hah
 */
export const submitExtrinsic = (network) => (extrinsic) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "POST",
        url: getSidecarUrl("/transaction"),
        data: {
            tx: extrinsic,
        },
    });
    return data.hash;
});
/**
 * Retrieve the transaction fees and weights
 * Note: fees on Substrate are not set by the signer, but directly by the blockchain runtime.
 *
 * @async
 * @param {string} extrinsic - the encoded extrinsic to send with a fake signing
 *
 * @returns {SidecarPaymentInfo}
 */
export const paymentInfo = (network) => (extrinsic) => __awaiter(void 0, void 0, void 0, function* () {
    const { data, } = yield network({
        method: "POST",
        url: getSidecarUrl("/transaction/fee-estimate"),
        data: {
            tx: extrinsic,
        },
    });
    return data;
});
/**
 * List all validators for the current era, and their exposure, and identity.
 *
 * @async
 * @param {string | string[]} stashes - stashes or validator status
 *
 * @returns {PolkadotValidator[]}
 */
export const getValidators = (network) => (stashes = "elected") => __awaiter(void 0, void 0, void 0, function* () {
    let validators;
    if (Array.isArray(stashes)) {
        validators = yield fetchValidators(network)("all", stashes);
    }
    else {
        validators = yield fetchValidators(network)(stashes);
    }
    return validators.map(v => ({
        address: v.accountId,
        identity: v.identity
            ? [v.identity.displayParent, v.identity.display].filter(Boolean).join(" - ").trim()
            : "",
        nominatorsCount: Number(v.nominatorsCount),
        rewardPoints: v.rewardsPoints ? new BigNumber(v.rewardsPoints) : null,
        commission: new BigNumber(v.commission).dividedBy(VALIDATOR_COMISSION_RATIO),
        totalBonded: new BigNumber(v.total),
        selfBonded: new BigNumber(v.own),
        isElected: v.isElected,
        isOversubscribed: v.isOversubscribed,
    }));
});
/**
 * Get Active Era progress
 *
 * @async
 *
 * @returns {PolkadotStakingProgress}
 */
export const getStakingProgress = (network, cache) => () => __awaiter(void 0, void 0, void 0, function* () {
    var _m, _o, _p;
    const [progress, consts] = yield Promise.all([
        fetchStakingProgress(network)(),
        getConstants(network, cache)(),
    ]);
    const activeEra = Number(progress.activeEra);
    const currentBlock = Number(progress.at.height);
    const toggleEstimate = Number((_m = progress.electionStatus) === null || _m === void 0 ? void 0 : _m.toggleEstimate);
    const electionClosed = !((_p = (_o = progress.electionStatus) === null || _o === void 0 ? void 0 : _o.status) === null || _p === void 0 ? void 0 : _p.Open);
    // Consider election open if in the THERSHOLD blocks before the real expected change
    // It disables staking flows that are subject to fail or block because of election status
    // update while user is signing
    const optimisticElectionClosed = electionClosed &&
        activeEra &&
        currentBlock &&
        toggleEstimate &&
        currentBlock >= toggleEstimate - ELECTION_STATUS_OPTIMISTIC_THRESHOLD
        ? false
        : electionClosed;
    return {
        activeEra,
        electionClosed: optimisticElectionClosed,
        maxNominatorRewardedPerValidator: Number(consts.staking.maxNominatorRewardedPerValidator) || 128,
        bondingDuration: Number(consts.staking.bondingDuration) || 28,
    };
});
/**
 * Create a new Registry for creating Polkadot.JS types (or any Substrate)
 *
 * @async
 *
 * @returns {Object} - { registry, extrinsics }
 */
export const getRegistry = (network, cache) => () => __awaiter(void 0, void 0, void 0, function* () {
    const [material, spec] = yield Promise.all([
        getTransactionMaterialWithMetadata(network, cache)(),
        fetchChainSpec(network)(),
    ]);
    const registry = new TypeRegistry();
    const metadata = new Metadata(registry, material.metadata);
    // Register types specific to chain/runtimeVersion
    registry.register(getSpecTypes(registry, material.chainName, material.specName, Number(material.specVersion)));
    // Register the chain properties for this registry
    registry.setChainProperties(registry.createType("ChainProperties", {
        ss58Format: Number(spec.properties.ss58Format),
        tokenDecimals: Number(spec.properties.tokenDecimals),
        tokenSymbol: spec.properties.tokenSymbol,
    }));
    registry.setMetadata(metadata);
    const extrinsics = expandMetadata(registry, metadata).tx;
    return {
        registry,
        extrinsics,
    };
});
/*
 * CACHED REQUESTS
 * NOTE: we don't use the cache from family's `cache.js` to avoid cyclic imports.
 */
/**
 * Cache the fetchConstants to avoid too many calls
 *
 * @async
 *
 * @returns {Promise<Object>} consts
 */
const getConstants = (network, cache) => cache(() => __awaiter(void 0, void 0, void 0, function* () { return fetchConstants(network)(); }), () => "polkadot", {
    max: 1,
    ttl: 60 * 60 * 1000, // 1 hour
});
/**
 * Cache the fetchTransactionMaterial(true) to avoid too many calls
 *
 * @async
 *
 * @returns {Promise<Object>} consts
 */
const getTransactionMaterialWithMetadata = (network, cache) => cache(() => __awaiter(void 0, void 0, void 0, function* () { return fetchTransactionMaterial(network)(true); }), () => "polkadot", {
    ttl: 60 * 60 * 1000, // 1 hour - could be Infinity
});
//# sourceMappingURL=sidecar.js.map